import { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCorporateDocuments, CorporateDocument, DocumentCategory } from "@/hooks/useCorporateDocuments";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { PDFPreview } from "@/components/corporate-documents/PDFPreview";
import { CategoryTabs, getCategoryLabel, getCategoryIcon } from "@/components/corporate-documents/CategoryTabs";
import { 
  BookOpen, 
  FileText, 
  ExternalLink,
  Calendar,
  User,
  Loader2,
  Eye,
  Search
} from "lucide-react";

const translations = {
  en: {
    title: "Corporate Documents",
    subtitle: "Training guides, policies and procedures for team members",
    referenceGuide: "Platform Reference Guide",
    referenceDesc: "Complete documentation of the AcquizGroup platform for freelancers and business partners",
    viewGuide: "View Guide",
    typeGuide: "Guide",
    typePolicy: "Policy",
    typeProcedure: "Procedure",
    typeTraining: "Training",
    noDocuments: "No documents in this category",
    loading: "Loading documents...",
    preview: "Preview",
    openNew: "Open",
    searchPlaceholder: "Search documents...",
  },
  fr: {
    title: "Documents Corporatifs",
    subtitle: "Guides de formation, politiques et procédures pour les membres de l'équipe",
    referenceGuide: "Guide de Référence Plateforme",
    referenceDesc: "Documentation complète de la plateforme AcquizGroup pour freelancers et partenaires d'affaires",
    viewGuide: "Voir le Guide",
    typeGuide: "Guide",
    typePolicy: "Politique",
    typeProcedure: "Procédure",
    typeTraining: "Formation",
    noDocuments: "Aucun document dans cette catégorie",
    loading: "Chargement des documents...",
    preview: "Aperçu",
    openNew: "Ouvrir",
    searchPlaceholder: "Rechercher des documents...",
  },
};

export default function StaffCorporateDocuments() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;
  const { documents, loading } = useCorporateDocuments();
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | 'all'>('all');
  const [previewDoc, setPreviewDoc] = useState<CorporateDocument | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesSearch = searchQuery === "" || 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getTypeBadgeColor = (type: CorporateDocument['type']) => {
    switch (type) {
      case 'guide': return 'bg-blue-500';
      case 'policy': return 'bg-purple-500';
      case 'procedure': return 'bg-green-500';
      case 'training': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  const getTypeLabel = (type: CorporateDocument['type']) => {
    switch (type) {
      case 'guide': return t.typeGuide;
      case 'policy': return t.typePolicy;
      case 'procedure': return t.typeProcedure;
      case 'training': return t.typeTraining;
      default: return type;
    }
  };

  const isPdfUrl = (url: string) => {
    return url.includes('.pdf') || url.includes('firebasestorage');
  };

  const isExternalUrl = (url: string) => {
    return url.startsWith('http://') || url.startsWith('https://');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">{t.title}</h1>
        <p className="text-gray-400 mt-1">{t.subtitle}</p>
      </div>

      {/* Main Reference Guide Card */}
      <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/30">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-amber-500 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-white text-xl">{t.referenceGuide}</CardTitle>
              <CardDescription className="text-gray-300">{t.referenceDesc}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Link to="/docs/reference">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                <ExternalLink className="h-4 w-4 mr-2" />
                {t.viewGuide}
              </Button>
            </Link>
            <Badge className="bg-blue-500">{t.typeGuide}</Badge>
            <span className="text-sm text-gray-400">v1.1 - Janvier 2025</span>
          </div>
        </CardContent>
      </Card>

      {/* Category Tabs and Search */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <CategoryTabs 
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          language={language}
        />
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.searchPlaceholder}
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          <span className="ml-2 text-gray-400">{t.loading}</span>
        </div>
      )}

      {/* Additional Documents Grid */}
      {!loading && filteredDocuments.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredDocuments.map((doc) => {
            const CategoryIcon = getCategoryIcon(doc.category);
            return (
              <Card key={doc.id} className="bg-white/5 border-white/10 hover:border-white/20 transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <CardTitle className="text-white text-base">{doc.title}</CardTitle>
                    </div>
                    <Badge className={getTypeBadgeColor(doc.type)}>
                      {getTypeLabel(doc.type)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                    <CategoryIcon className="h-3 w-3" />
                    {getCategoryLabel(doc.category, language)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-400 line-clamp-2">{doc.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {doc.created_at}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {doc.created_by}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    {isPdfUrl(doc.url) && isExternalUrl(doc.url) && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => setPreviewDoc(doc)}
                        className="text-gray-300 border-gray-600 hover:bg-white/10"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        {t.preview}
                      </Button>
                    )}
                    {isExternalUrl(doc.url) ? (
                      <a href={doc.url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="text-gray-300 border-gray-600 hover:bg-white/10">
                          <ExternalLink className="h-3 w-3 mr-1" />
                          {t.openNew}
                        </Button>
                      </a>
                    ) : (
                      <Link to={doc.url}>
                        <Button size="sm" variant="outline" className="text-gray-300 border-gray-600 hover:bg-white/10">
                          <ExternalLink className="h-3 w-3 mr-1" />
                          {t.viewGuide}
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && filteredDocuments.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>{t.noDocuments}</p>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewDoc && (
        <PDFPreview
          url={previewDoc.url}
          title={previewDoc.title}
          isOpen={!!previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}
